import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X, Download } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import renderPdf from "../RenderPdf/RenderPdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const DocumentPreviewModal = ({ isOpen, onClose, document, onDownload }) => {
  const pdfCanvasRef = useRef(null);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && document) {
      setError(null);
      setPdfLoaded(false);
      setPdfLoading(document.type === "pdf");
    }
  }, [isOpen, document]);

  useEffect(() => {
    let mounted = true;
    let timeoutId;

    const loadPdf = async () => {
      if (!isOpen || !document || document.type !== "pdf") return;

      timeoutId = setTimeout(async () => {
        try {
          if (!pdfCanvasRef.current) {
            throw new Error("Canvas not available");
          }

          await renderPdf(document.url, pdfCanvasRef, 1.0);

          if (mounted) {
            setPdfLoaded(true);
            setPdfLoading(false);
          }
        } catch (err) {
          if (mounted) {
            setError("Failed to render PDF preview");
            setPdfLoading(false);
          }
        }
      }, 300);
    };

    loadPdf();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [isOpen, document]);

  if (!document) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all flex flex-col">
                <div className="flex items-center justify-between p-4 border-b">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {document.title || "Document Preview"}
                  </Dialog.Title>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onDownload?.(document)}
                      className="flex items-center gap-1 text-sm font-medium text-[#3538CD] hover:text-indigo-700 focus:outline-none"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>

                    <button
                      onClick={onClose}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto bg-gray-50 p-4 flex items-center justify-center relative">
                  {pdfLoading && (
                    <div className="text-center text-gray-500">
                      <svg
                        className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <p>Loading PDF preview…</p>
                    </div>
                  )}

                  {error && (
                    <div className="text-center">
                      <p className="text-red-500 mb-3">{error}</p>
                      <button
                        onClick={() =>
                          window.open(
                            document.url,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        className="px-4 py-2 bg-[#3538CD] text-white rounded-md"
                      >
                        Open in new tab
                      </button>
                    </div>
                  )}

                  {document.type === "image" && !error && (
                    <img
                      src={document.url}
                      alt={document.title}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
                      onError={() => setError("Failed to load image")}
                    />
                  )}

                  {document.type === "pdf" && (
                    <canvas
                      ref={pdfCanvasRef}
                      className={`${
                        pdfLoaded ? "block" : "hidden"
                      } max-w-full max-h-[65vh]`}
                      style={{ boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                    />
                  )}
                </div>

                <div className="flex justify-end p-4 border-t bg-gray-50">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DocumentPreviewModal;
